/**
 * ClimbBottomBar Component
 *
 * Fixed bottom bar for the Climb tab showing:
 * - Progress pill with ring and counter (centered)
 * - Separate FAB toggle for list/map (right-aligned, position matches route card header)
 *
 * Positioned above the main tab bar. Toggle position aligns with route card
 * header toggle for seamless visual transition.
 */

import { type CSSProperties } from 'react';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import MapIcon from '@mui/icons-material/Map';
import { ProgressRing } from './ProgressRing';

export type ViewMode = 'list' | 'map';

export interface ClimbBottomBarProps {
  sendsCount: number;
  totalRoutes: number;
  progressPercent: number;
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  hasFloorPlan: boolean;
  loading?: boolean;
  /** Whether a route is currently selected (hides entire bottom bar to avoid overlap with route card) */
  isRouteSelected?: boolean;
}

export const ClimbBottomBar = ({
  sendsCount,
  totalRoutes,
  progressPercent,
  viewMode,
  onViewModeChange,
  hasFloorPlan,
  loading = false,
  isRouteSelected = false,
}: ClimbBottomBarProps) => {
  const counterText = loading ? '—/—' : `${sendsCount}/${totalRoutes}`;

  // Toggle to the opposite view
  const handleToggle = () => {
    onViewModeChange(viewMode === 'list' ? 'map' : 'list');
  };

  // Disable toggle FAB when on list view and no floor plan exists
  const isToggleDisabled = viewMode === 'list' && !hasFloorPlan;

  // Hide both elements when route card is open (they overlap with the card)
  if (isRouteSelected) {
    return null;
  }

  return (
    <>
      {/* Progress pill - positioned bottom left */}
      <div id="progress-ring" style={progressPillStyle}>
        <ProgressRing percent={loading ? 0 : progressPercent} size={28} strokeWidth={2.5} />
        <div style={progressInfoStyle}>
          <span style={circuitsLabelStyle}>All circuits</span>
          <span style={counterStyle}>{counterText}</span>
        </div>
      </div>

      {/* FAB toggle - positioned bottom right */}
      <button
        id="view-toggle"
        type="button"
        onClick={handleToggle}
        aria-label={viewMode === 'list' ? 'Switch to map view' : 'Switch to list view'}
        disabled={isToggleDisabled}
        style={{
          ...fabToggleStyle,
          ...(isToggleDisabled ? disabledFabStyle : {}),
        }}
      >
        {viewMode === 'list' ? (
          <MapIcon style={fabIconStyle} />
        ) : (
          <FormatListBulletedIcon style={fabIconStyle} />
        )}
      </button>
    </>
  );
};

// Progress pill - positioned bottom left (uses CSS vars for light/dark mode)
const progressPillStyle: CSSProperties = {
  position: 'fixed',
  bottom: 70,
  left: 16,
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '8px 16px',
  borderRadius: '24px',
  backgroundColor: 'var(--color-bg-primary, white)',
  boxShadow: '0 2px 12px rgba(0, 0, 0, 0.15)',
  zIndex: 1050,
};

const progressInfoStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '2px',
};

const circuitsLabelStyle: CSSProperties = {
  fontSize: '0.6875rem',
  color: 'var(--color-text-secondary, #6b7280)',
};

const counterStyle: CSSProperties = {
  fontSize: '0.8125rem',
  fontWeight: 600,
  color: 'var(--color-text-primary, #1f2937)',
};

// FAB toggle - positioned bottom right (uses CSS vars for light/dark mode)
const fabToggleStyle: CSSProperties = {
  position: 'fixed',
  bottom: 70,
  right: 16,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '44px',
  height: '44px',
  border: 'none',
  borderRadius: '50%',
  backgroundColor: 'var(--color-bg-primary, white)',
  boxShadow: '0 2px 12px rgba(0, 0, 0, 0.15)',
  color: 'var(--color-text-primary, #1f2937)',
  cursor: 'pointer',
  transition: 'all 0.15s ease',
  zIndex: 1050,
};

const disabledFabStyle: CSSProperties = {
  opacity: 0.5,
  cursor: 'not-allowed',
};

const fabIconStyle: CSSProperties = {
  fontSize: '1.25rem',
};

export default ClimbBottomBar;
